import React, { useState } from 'react';
import { X, Key, User, Mail, Phone, CheckCircle } from 'lucide-react';
import api from './api'
import { toast } from 'react-toastify';

const CreateEnseignantAccountModal = ({ 
  enseignant, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [usernameType, setUsernameType] = useState('matricule');
  const [loading, setLoading] = useState(false);
  const [createdAccount, setCreatedAccount] = useState(null);

  if (!isOpen || !enseignant) return null;

  const handleCreateAccount = async () => {
    setLoading(true);
    try {
      const response = await api.post('/utilisateurs/enseignant/create/', {
        enseignant_id: enseignant.id,
        username_type: usernameType
      });

      if (response.data) {
        setCreatedAccount(response.data);
        toast.success('Compte enseignant créé avec succès !');
        onSuccess && onSuccess();
      }
    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      const errorMessage = error.response?.data?.error || 'Erreur lors de la création du compte';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCreatedAccount(null);
    onClose();
  };

  const getUsernameValue = () => {
    switch (usernameType) {
      case 'matricule':
        return enseignant.matricule || 'Non défini';
      case 'email':
        return enseignant.email || 'Non défini';
      case 'telephone':
        return enseignant.telephone || 'Non défini';
      default:
        return 'Non défini';
    }
  };

  const getAvailableUsernameTypes = () => {
    const types = [];
    if (enseignant.matricule) types.push('matricule');
    if (enseignant.email) types.push('email');
    if (enseignant.telephone) types.push('telephone');
    return types;
  };

  const availableTypes = getAvailableUsernameTypes();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <Key className="h-5 w-5 mr-2 text-blue-600" />
            Créer un compte enseignant
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!createdAccount ? (
            <>
              {/* Informations de l'enseignant */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-3">Informations de l'enseignant</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <div><span className="font-medium">Nom:</span> {enseignant.nom} {enseignant.prenom}</div>
                  <div><span className="font-medium">Matricule:</span> {enseignant.matricule || 'Non défini'}</div>
                  <div><span className="font-medium">Email:</span> {enseignant.email || 'Non défini'}</div>
                  <div><span className="font-medium">Téléphone:</span> {enseignant.telephone || 'Non défini'}</div>
                </div>
              </div>

              {/* Sélection du type de username */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Choisir le nom d'utilisateur
                </label>
                <div className="space-y-3">
                  {availableTypes.includes('matricule') && (
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="usernameType"
                        value="matricule"
                        checked={usernameType === 'matricule'}
                        onChange={(e) => setUsernameType(e.target.value)}
                        className="mr-3 text-blue-600"
                      />
                      <div className="flex items-center">
                        <User className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-sm">Matricule: {enseignant.matricule}</span>
                      </div>
                    </label>
                  )}
                  
                  {availableTypes.includes('email') && (
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="usernameType"
                        value="email"
                        checked={usernameType === 'email'}
                        onChange={(e) => setUsernameType(e.target.value)}
                        className="mr-3 text-blue-600"
                      />
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-sm">Email: {enseignant.email}</span>
                      </div>
                    </label>
                  )}
                  
                  {availableTypes.includes('telephone') && (
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="usernameType"
                        value="telephone"
                        checked={usernameType === 'telephone'}
                        onChange={(e) => setUsernameType(e.target.value)}
                        className="mr-3 text-blue-600"
                      />
                      <div className="flex items-center">
                        <Phone className="h-4 w-4 mr-2 text-gray-500" />
                        <span className="text-sm">Téléphone: {enseignant.telephone}</span>
                      </div>
                    </label>
                  )}
                </div>
                
                {availableTypes.length === 0 && (
                  <div className="text-red-600 text-sm mt-2">
                    Aucune information disponible pour créer un nom d'utilisateur
                  </div>
                )}
              </div>

              {/* Aperçu du username */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Nom d'utilisateur choisi:</h4>
                <div className="text-blue-800 font-mono text-lg">{getUsernameValue()}</div>
              </div>

              {/* Bouton de création */}
              <button
                onClick={handleCreateAccount}
                disabled={loading || availableTypes.length === 0}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  loading || availableTypes.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {loading ? 'Création en cours...' : 'Créer le compte'}
              </button>
            </>
          ) : (
            /* Affichage du compte créé */
            <div className="text-center">
              <div className="mb-4">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Compte créé avec succès !
              </h3>
              
              <div className="mb-6 p-4 bg-green-50 rounded-lg text-left">
                <h4 className="font-medium text-green-900 mb-3">Informations de connexion:</h4>
                <div className="space-y-2 text-sm">
                  <div><span className="font-medium">Nom d'utilisateur:</span> {createdAccount.user.username}</div>
                  <div><span className="font-medium">Mot de passe:</span> 
                    <span className="font-mono text-green-800 bg-green-100 px-2 py-1 rounded ml-2">
                      {createdAccount.password}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 mb-6">
                <p className="font-medium text-red-600">⚠️ Important:</p>
                <p>Notez ce mot de passe et partagez-le avec l'enseignant.</p>
                <p>Il ne sera plus visible après la fermeture de cette fenêtre.</p>
              </div>
              
              <button
                onClick={handleClose}
                className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateEnseignantAccountModal;
